import { useCallback, useState, useEffect, useRef } from 'react';
import { FileRejection } from 'react-dropzone';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useUploadProductImageByAdminMutation } from '@/store/services/products';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UploadedImage {
  id: string;
  file?: File;
  preview: string;
  uploading?: boolean;
}

export interface UploadResponse {
  payload: {
    id: string;
    blobFileUrl: string;
    blobName?: string;
    blobSize?: number;
    extension?: string;
  }[];
}

export interface UseImageUploaderProps {
  maxFiles: number;
  maxSizeMB: number;
  initialImages: UploadedImage[];
  onImagesChange?: (images: UploadedImage[]) => void;
  onThumbnailChange?: (thumbnailId: string | null) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

export function toIdString(images: UploadedImage[]) {
  return images.map((i) => i.id).join(',');
}

// ─── Custom Hook ──────────────────────────────────────────────────────────────

export function useImageUploader({
  maxFiles,
  maxSizeMB,
  initialImages,
  onImagesChange,
  onThumbnailChange,
}: UseImageUploaderProps) {
  const maxFileSize = maxSizeMB * 1024 * 1024; // kept internal, not returned

  const [images, setImages] = useState<UploadedImage[]>(initialImages);
  const [rejections, setRejections] = useState<FileRejection[]>([]);

  // Auto-dismiss rejection warnings after 5 s
  useEffect(() => {
    if (rejections.length === 0) return;
    const timer = setTimeout(() => setRejections([]), 4_000);
    return () => clearTimeout(timer);
  }, [rejections]);

  // Derived — no useState needed; every handler already keeps the thumbnail at index 0
  const thumbnailId = images[0]?.id ?? null;

  const lastEmittedIdsRef = useRef(toIdString(initialImages));

  const [uploadImages] = useUploadProductImageByAdminMutation();

  // Sync with initialImages only when the parent provides a genuinely new set,
  // not when we triggered the update ourselves via onImagesChange
  useEffect(() => {
    const incomingIds = toIdString(initialImages);
    const currentIds  = toIdString(images);

    const hasExternalUpdate =
      incomingIds !== currentIds &&
      incomingIds !== lastEmittedIdsRef.current;

    if (hasExternalUpdate) {
      setImages(initialImages);
      lastEmittedIdsRef.current = incomingIds;
    }
  }, [initialImages, images]);

  // Single write-point: update the ref, notify parent, and return next for chaining
  const commitImages = useCallback(
    (next: UploadedImage[]) => {
      lastEmittedIdsRef.current = toIdString(next);
      onImagesChange?.(next);
      // Notify thumbnail change — derived from index 0, no separate state needed
      onThumbnailChange?.(next[0]?.id ?? null);
      return next;
    },
    [onImagesChange, onThumbnailChange]
  );

  // Revokes a blob URL only if it's a local object URL (not a remote server URL)
  function revokeBlobUrl(url: string) {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setRejections([]);

      const remaining      = maxFiles - images.length;
      const filesToUpload  = acceptedFiles.slice(0, remaining);
      if (filesToUpload.length === 0) return;

      // Build optimistic entries and show them immediately while uploading
      const optimisticItems: UploadedImage[] = filesToUpload.map((file) => ({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
        uploading: true,
      }));

      setImages((prev) => commitImages([...prev, ...optimisticItems]));

      const formData = new FormData();
      filesToUpload.forEach((file) => formData.append('files', file));

      try {
        const response = await uploadImages(formData).unwrap();
        if (!response?.payload) throw new Error('Invalid response');

        // O(1) lookup: map optimistic id → server payload item
        const serverItemByOptimisticId = new Map(
          optimisticItems.map((item, index) => [item.id, response.payload[index]])
        );

        setImages((prev) => {
          const next = prev.map((img) => {
            const serverItem = serverItemByOptimisticId.get(img.id);
            if (!serverItem) return img;

            // Revoke the blob URL now that we have the permanent server URL
            revokeBlobUrl(img.preview);

            return {
              ...img,
              id:       serverItem.id,
              preview:  serverItem.blobFileUrl || img.preview,
              uploading: false,
            };
          });

          return commitImages(next);
        });
      } catch (err) {
        console.error('ImageUploader: upload failed', err);

        // Roll back: remove optimistic entries and release their blob URLs
        const optimisticIds = new Set(optimisticItems.map((o) => o.id));

        setImages((prev) => {
          const next = prev.filter((img) => {
            if (optimisticIds.has(img.id)) {
              revokeBlobUrl(img.preview);
              return false;
            }
            return true;
          });

          return commitImages(next);
        });
      }
    },
    [maxFiles, images.length, uploadImages, commitImages]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setImages((prev) => {
        const oldIndex = prev.findIndex((i) => i.id === active.id);
        const newIndex = prev.findIndex((i) => i.id === over.id);
        return commitImages(arrayMove(prev, oldIndex, newIndex));
      });
    },
    [commitImages]
  );

  const handleSetThumbnail = useCallback(
    (id: string) => {
      // Moving to index 0 is what makes it the thumbnail (derived state)
      setImages((prev) => {
        const index = prev.findIndex((i) => i.id === id);
        if (index <= 0) return prev;
        return commitImages(arrayMove(prev, index, 0));
      });
    },
    [commitImages]
  );

  const handleRemove = useCallback(
    (id: string) => {
      setImages((prev) => {
        const target = prev.find((i) => i.id === id);
        if (target) revokeBlobUrl(target.preview);

        return commitImages(prev.filter((i) => i.id !== id));
      });
    },
    [commitImages]
  );

  return {
    images,
    thumbnailId,   // derived, read-only — consumers don't need to know that
    rejections,
    maxFileSize,   // byte value for useDropzone; named to match the prop it came from
    setRejections,
    onDrop,
    handleDragEnd,
    handleSetThumbnail,
    handleRemove,
  };
}
